<template>
    <v-card v-if="priceListForEdit">
        <v-card-text>
            <v-form ref="form" v-model="isValidGeneral" :disabled="isSaveLoading" data-cy="product-prices-extra-form">
                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="priceListForEdit.name"
                            :rules="[requiredRule('name')]"
                            density="compact"
                            hide-details
                            :label="`${$t('name')}*`"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <div class="full-width d-flex mt-1">
                            <v-spacer />
                            <v-switch
                                v-model="priceListForEdit.active"
                                color="primary"
                                hide-details
                                :label="$t('active')"
                            />
                        </div>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="12">
                        <v-card variant="outlined" :loading="isTariffsLoading">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('tariffs') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-data-table
                                    :items="priceListForEdit.tariffLinks"
                                    :headers="headersTariffs"
                                    :hide-default-header="isMobile"
                                    hide-default-footer
                                    :items-per-page="-1"
                                >
                                    <template v-slot:item.tariff="{item}">
                                        <div class="my-3">
                                            <v-select
                                                v-model="item.tariffId"
                                                :items="tariffItems(item.tariffId)"
                                                item-value="id"
                                                item-title="name"
                                                hide-details
                                                :density="'compact'"
                                                :disabled="item.tariffId && !tariffStatus(item.tariffId)"
                                            />
                                        </div>
                                    </template>
                                    <template v-slot:item.active="{item}">
                                        {{ tariffStatus(item.tariffId) ? $t('active') : $t('inactive') }}
                                    </template>
                                    <template v-slot:item.dates="{item}">
                                        <div class="d-flex align-center">
                                            <div class="me-4 full-width">
                                                <date-picker
                                                    v-model="item.dateFrom"
                                                    outlined
                                                    :density="'compact'"
                                                    :max="item.dateTo || '2100-01-01'"
                                                    :label="$t('date_from') + '*'"
                                                    :rules="[requiredRule('date_from')]"
                                                    hide-details
                                                />
                                            </div>
                                            <div class="full-width">
                                                <date-picker
                                                    v-model="item.dateTo"
                                                    outlined
                                                    :min="item.dateFrom || '1900-01-01'"
                                                    :density="'compact'"
                                                    :label="$t('date_to') + '*'"
                                                    :rules="[requiredRule('date_to')]"
                                                    hide-details
                                                />
                                            </div>
                                        </div>
                                    </template>
                                    <template v-slot:item.minDays="{item}">
                                        <v-number-input
                                            v-model="item.minDays"
                                            :min="0"
                                            hide-details
                                            density="compact"
                                        />
                                    </template>
                                    <template v-slot:item.maxDays="{item}">
                                        <v-number-input
                                            v-model="item.maxDays"
                                            :min="0"
                                            hide-details
                                            density="compact"
                                        />
                                    </template>
                                    <template v-slot:item.actions="{item, index}">
                                        <div class="full-width d-flex align-center">
                                            <v-spacer />
                                            <v-btn
                                                variant="text"
                                                size="small"
                                                color="primary"
                                                @click="copyTariffLink(item)"
                                            >
                                                {{ $t('copy') }}
                                            </v-btn>
                                            <v-btn
                                                size="small"
                                                color="error"
                                                variant="text"
                                                icon="mdi-close"
                                                @click="removeTariffLink(index)"
                                            />
                                        </div>
                                    </template>
                                    <template v-slot:item.mobile="{item, index}">
                                        <v-col cols="12" class="mt-4 px-0">
                                            <v-select
                                                v-model="item.tariffId"
                                                :items="tariffItems(item.tariffId)"
                                                item-value="id"
                                                item-title="name"
                                                hide-details
                                                :density="'compact'"
                                                :disabled="item.tariffId && !tariffStatus(item.tariffId)"
                                            />
                                            <div class="text-caption">
                                                {{ tariffStatus(item.tariffId) ? $t('active') : $t('inactive') }}
                                            </div>
                                        </v-col>
                                        <v-col cols="12" class="px-0">
                                            <date-picker
                                                v-model="item.dateFrom"
                                                outlined
                                                :density="'compact'"
                                                :max="item.dateTo || '2100-01-01'"
                                                :label="$t('date_from') + '*'"
                                                :rules="[requiredRule('date_from')]"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col cols="12" class="px-0">
                                            <date-picker
                                                v-model="item.dateTo"
                                                outlined
                                                :min="item.dateFrom || '1900-01-01'"
                                                :density="'compact'"
                                                :label="$t('date_to') + '*'"
                                                :rules="[requiredRule('date_to')]"
                                                hide-details
                                            />
                                        </v-col>
                                        <template v-if="isShowMinMaxNights">
                                            <v-col cols="12" class="px-0">
                                                <v-number-input
                                                    v-model="item.minDays"
                                                    :min="0"
                                                    hide-details
                                                    density="compact"
                                                    :label="$t('number_of_nights_min')"
                                                />
                                            </v-col>
                                            <v-col cols="12" class="px-0">
                                                <v-number-input
                                                    v-model="item.maxDays"
                                                    :min="0"
                                                    hide-details
                                                    density="compact"
                                                    :label="$t('number_of_nights_max')"
                                                />
                                            </v-col>
                                        </template>
                                        <v-col cols="12" class="px-0">
                                            <div class="full-width d-flex align-center">
                                                <v-spacer />
                                                <v-btn
                                                    variant="text"
                                                    size="small"
                                                    color="primary"
                                                    @click="copyTariffLink(item)"
                                                >
                                                    {{ $t('copy') }}
                                                </v-btn>
                                                <v-btn
                                                    size="small"
                                                    color="error"
                                                    variant="text"
                                                    icon="mdi-close"
                                                    @click="removeTariffLink(index)"
                                                />
                                            </div>
                                        </v-col>
                                    </template>
                                </v-data-table>
                                <v-divider class="mb-4" />
                                <v-row dense>
                                    <v-col cols="12">
                                        <div class="full-width d-flex align-center">
                                            <v-btn variant="flat" size="small" color="primary" @click="addTariffLink">
                                                {{ $t('add') }}
                                            </v-btn>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="12">
                        <v-card variant="outlined">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('terms_of_use') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-data-table
                                    :items="priceListForEdit.bookingDates"
                                    :headers="headersTermsOfUse"
                                    :hide-default-header="isMobile"
                                    hide-default-footer
                                    :items-per-page="-1"
                                >
                                    <template v-slot:item.date_type="{item}">
                                        <div class="my-3">
                                            <v-select
                                                v-model="item.condition"
                                                :items="conditionItems"
                                                hide-details
                                                :rules="[requiredRule('booking_date')]"
                                                :density="'compact'"
                                                @update:model-value="onConditionChange(item)"
                                            />
                                        </div>
                                    </template>
                                    <template v-slot:item.dates="{item}">
                                        <div class="d-flex align-center">
                                            <div v-if="'date' in item" class="full-width">
                                                <date-picker
                                                    v-model="item.date"
                                                    outlined
                                                    dense
                                                    :rules="[requiredRule('date')]"
                                                    :density="'compact'"
                                                    :label="$t('date') + '*'"
                                                    hide-details
                                                />
                                            </div>

                                            <div v-if="'dateFrom' in item" class="me-4 full-width">
                                                <date-picker
                                                    v-model="item.dateFrom"
                                                    outlined
                                                    :max="item.dateTo || '2100-01-01'"
                                                    :rules="[requiredRule('date_from')]"
                                                    :density="'compact'"
                                                    :label="$t('date_from') + '*'"
                                                    hide-details
                                                />
                                            </div>
                                            <div v-if="'dateTo' in item" class="full-width">
                                                <date-picker
                                                    v-model="item.dateTo"
                                                    outlined
                                                    :min="item.dateFrom || '1900-01-01'"
                                                    :density="'compact'"
                                                    :rules="[requiredRule('date_to')]"
                                                    :label="$t('date_to') + '*'"
                                                    hide-details
                                                />
                                            </div>
                                        </div>
                                    </template>
                                    <template v-slot:item.actions="{index}">
                                        <div class="full-width d-flex align-center">
                                            <v-spacer />
                                            <v-btn
                                                size="small"
                                                color="error"
                                                variant="text"
                                                icon="mdi-close"
                                                @click="removeBookingDate(index)"
                                            />
                                        </div>
                                    </template>
                                    <template v-slot:item.mobile="{item, index}">
                                        <v-col cols="12" class="mt-4 px-0">
                                            <v-select
                                                v-model="item.condition"
                                                :items="conditionItems"
                                                hide-details
                                                :rules="[requiredRule('booking_date')]"
                                                :density="'compact'"
                                                @update:model-value="onConditionChange(item)"
                                            />
                                        </v-col>
                                        <v-col v-if="'date' in item" cols="12" class="px-0">
                                            <date-picker
                                                v-model="item.date"
                                                outlined
                                                dense
                                                :rules="[requiredRule('date')]"
                                                :density="'compact'"
                                                :label="$t('date') + '*'"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col v-if="'dateFrom' in item" cols="12" class="px-0">
                                            <date-picker
                                                v-model="item.dateFrom"
                                                outlined
                                                :max="item.dateTo || '2100-01-01'"
                                                :rules="[requiredRule('date_from')]"
                                                :density="'compact'"
                                                :label="$t('date_from') + '*'"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col v-if="'dateTo' in item" cols="12" class="px-0">
                                            <date-picker
                                                v-model="item.dateTo"
                                                outlined
                                                :min="item.dateFrom || '1900-01-01'"
                                                :density="'compact'"
                                                :rules="[requiredRule('date_to')]"
                                                :label="$t('date_to') + '*'"
                                                hide-details
                                            />
                                        </v-col>

                                        <v-col cols="12" class="px-0">
                                            <div class="full-width d-flex align-center">
                                                <v-spacer />
                                                <v-btn
                                                    size="small"
                                                    color="error"
                                                    variant="text"
                                                    icon="mdi-close"
                                                    @click="removeBookingDate(index)"
                                                />
                                            </div>
                                        </v-col>
                                    </template>
                                </v-data-table>
                                <v-divider class="mb-4" />
                                <v-row dense>
                                    <v-col cols="12">
                                        <div class="full-width d-flex align-center">
                                            <v-btn variant="flat" size="small" color="primary" @click="addBookingDate">
                                                {{ $t('add') }}
                                            </v-btn>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="12">
                        <v-card variant="outlined" :loading="isOwnProductExtraServicesLoading">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('price_for_extra_services') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-data-table
                                    :items="priceListForEdit.extraServicePrices"
                                    :headers="headersPriceExtra"
                                    hide-default-footer
                                    :items-per-page="-1"
                                    :loading="isOwnProductExtraServicesLoading"
                                >
                                    <template v-slot:item.name="{item}">
                                        <span v-if="getExtraService(item)">
                                            {{ getExtraService(item).name[selectedLocale] }}
                                        </span>
                                    </template>
                                    <template v-slot:item.active="{item}">
                                        <span v-if="getExtraService(item)">
                                            {{ getExtraService(item).active ? $t('active') : $t('inactive') }}
                                        </span>
                                    </template>
                                    <template v-slot:item.priceCalculationType="{item}">
                                        {{ priceCalculationNote(item) }}
                                    </template>
                                    <template v-slot:item.includeInFee="{item}">
                                        <v-checkbox
                                            v-model="item.includeInFee"
                                            :disabled="item.availability === 'NOT_AVAILABLE' || item.includeInFee"
                                            hide-details
                                        />
                                    </template>
                                    <template v-slot:item.includeInMarkUpAndCommission="{item}">
                                        <v-checkbox
                                            v-model="item.includeInMarkUpAndCommission"
                                            :disabled="item.availability === 'NOT_AVAILABLE'"
                                            hide-details
                                        />
                                    </template>
                                    <template v-slot:item.availability="{item}">
                                        <div class="my-3">
                                            <v-select
                                                v-model="item.availability"
                                                :items="priceExtraAvailabilityItems"
                                                :label="$t('availability')"
                                                hide-details
                                                :width="200"
                                            />
                                        </div>
                                    </template>
                                    <template v-slot:item.amount="{item}">
                                        <div class="py-2">
                                            <NumberInputWithButtons
                                                v-model="item.amount"
                                                :label="item.percentage ? `${$t('percent')}*` : `${$t('amount')}*`"
                                                :disabled="item.availability === 'NOT_AVAILABLE'"
                                                :width="200"
                                                hide-details
                                                :buttons="[
                                                    getExtraService(item)?.calculationType !== 'BY_DAY' && {
                                                        icon: 'mdi-percent',
                                                        active: item.percentage,
                                                        onClick: () => (item.percentage = !item.percentage),
                                                    },
                                                    {
                                                        icon: 'mdi-cash',
                                                        active: !item.percentage,
                                                        onClick: () => {
                                                            if (getExtraService(item)?.calculationType !== 'BY_DAY') {
                                                                item.percentage = !item.percentage
                                                            }
                                                        },
                                                    },
                                                ]"
                                                :rules="item.availability === 'NOT_AVAILABLE' ? [] : [requiredRule('')]"
                                                :append-text="!item.percentage ? tariffCurrency : null"
                                            />
                                            <template
                                                v-if="
                                                    getExtraService(item)?.calculationType === 'BY_SERVICE' &&
                                                    item.percentage
                                                "
                                            >
                                                <div class="pt-2">
                                                    <v-select
                                                        v-model="item.priceCalculationType"
                                                        :items="priceCalculationType"
                                                        hide-details
                                                        :width="200"
                                                    />
                                                </div>
                                                <template v-if="contract.serviceType === 'ownHotel'">
                                                    <v-checkbox
                                                        v-model="item.calculationForProductService"
                                                        :label="$t('allocation').toLowerCase()"
                                                        hide-details
                                                        density="compact"
                                                        :rules="
                                                            !item.calculationForProductService &&
                                                            !item.calculationForProductSecondaryService
                                                                ? [requiredRule('')]
                                                                : []
                                                        "
                                                    />
                                                    <v-checkbox
                                                        v-model="item.calculationForProductSecondaryService"
                                                        :label="$t('meal')"
                                                        hide-details
                                                        density="compact"
                                                        :rules="
                                                            !item.calculationForProductService &&
                                                            !item.calculationForProductSecondaryService
                                                                ? [requiredRule('')]
                                                                : []
                                                        "
                                                    />
                                                    <div
                                                        v-if="
                                                            !item.calculationForProductService &&
                                                            !item.calculationForProductSecondaryService
                                                        "
                                                        class="text-error"
                                                    >
                                                        {{ $t('price_for_extras_calculation_validation_error') }}
                                                    </div>
                                                </template>
                                            </template>
                                        </div>
                                    </template>
                                </v-data-table>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="12">
                        <v-card variant="outlined">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('description') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-textarea
                                    v-model="priceListForEdit.description"
                                    :placeholder="`${$t('description')}`"
                                    rows="3"
                                />
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions class="d-flex justify-start px-6 pb-6 pt-4">
            <v-btn color="primary" :loading="isSaveLoading" data-cy="save" variant="elevated" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="close" variant="text" @click="onClose">
                {{ $t('cancel') }}
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import {clone} from '@/utils/helpers'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import DaterangeM from '~src/components/search/forms/daterangeM.src'
    import {contractServiceType} from '~/utils/types/types'

    @Component({
        components: {DaterangeM, DatePicker},
    })
    export class ProductPricesExtrasModal extends mixins(FormValidationRulesMixin) {
        @Prop() priceList
        @Prop() contract
        @Prop() selectedLocale

        isValidGeneral = false

        priceListForEdit = null

        activeTariffs = []
        inactiveTariffs = []
        isTariffsLoading = false

        ownProductExtraServices = []
        isOwnProductExtraServicesLoading = false

        isSaveLoading = false

        editMode = false

        async mounted() {
            await this.initValues()
        }

        async initValues() {
            if (this.priceList) {
                this.priceListForEdit = clone(this.priceList)
            } else {
                this.priceListForEdit = {
                    contractId: this.contract.id,
                    name: '',
                    description: '',
                    bookingDates: [],
                    active: true,
                    extraServicePrices: [],
                    tariffLinks: [],
                }
            }

            this.editMode = !!this.priceListForEdit.id

            await Promise.all([this.loadTariffs(), this.loadOwnProductExtraServices()])

            if (this.editMode === false) {
                if (!this.priceListForEdit.tariffLinks.length && this.activeTariffs.length) {
                    this.addTariffLink()
                }
            }
            if (this.ownProductExtraServices.length) {
                this.priceListForEdit.extraServicePrices = this.updateExtraServicePrices()
            }
        }

        updateExtraServicePrices() {
            const extraServicePrices = []
            this.ownProductExtraServices.forEach(extra => {
                const existingItem = this.priceListForEdit.extraServicePrices.find(
                    item => item.extraServiceId === extra.id
                )
                if (!existingItem) {
                    extraServicePrices.push({
                        extraServiceId: extra.id,
                        availability: 'NOT_AVAILABLE',
                        priceCalculationType: this.priceCalculationType[0].value,
                        amount: null,
                        percentage: false,
                        includeInFee: true,
                        includeInMarkUpAndCommission: true,
                        ...(this.contract.serviceType === contractServiceType.ownHotel && {
                            calculationForProductService: true,
                            calculationForProductSecondaryService: true,
                        }),
                    })
                } else extraServicePrices.push(existingItem)
            })
            return extraServicePrices
        }

        clear() {
            this.priceListForEdit = null
            this.activeTariffs = []
            this.inactiveTariffs = []
            this.ownProductExtraServices = []
        }

        get tariffs() {
            return [...this.activeTariffs, ...this.inactiveTariffs]
        }

        tariffItems(id) {
            return !id || this.tariffStatus(id) ? this.activeTariffs : this.inactiveTariffs
        }

        tariffStatus(id) {
            return !!this.tariffs.find(tariff => tariff.id === id)?.active
        }

        async loadTariffs() {
            try {
                this.isTariffsLoading = true
                const [activeTariffs, inactiveTariffs] = await Promise.all([
                    this.$api.tariffs.get({contractId: this.contract.id, active: true}),
                    this.$api.tariffs.get({contractId: this.contract.id, active: false}),
                ])
                this.activeTariffs = activeTariffs.tariffs
                this.inactiveTariffs = inactiveTariffs.tariffs
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isTariffsLoading = false
            }
        }

        async loadOwnProductExtraServices() {
            try {
                this.isOwnProductExtraServicesLoading = true
                this.ownProductExtraServices = (
                    await this.$api.ownProductExtraServices.get({contractId: this.contract.id, active: true})
                ).extraServices
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isOwnProductExtraServicesLoading = false
            }
        }

        @Emit()
        onClose() {
            this.clear()
        }

        async save() {
            await this.$refs.form.validate()
            if (!this.isValidGeneral) return

            if (this.editMode) {
                await this.saveEdit()
            } else {
                await this.saveCreate()
            }
        }

        @Emit()
        onSave() {}

        async saveEdit() {
            try {
                this.isSaveLoading = true

                await this.$api.ownProductPriceListExtras.put(this.priceListForEdit)

                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.onClose()
                this.onSave()
            } catch (e) {
                this.$toast.error(e.msg || e.errors[0].message || this.$t('error_message.save_error'))
            } finally {
                this.isSaveLoading = false
            }
        }

        async saveCreate() {
            try {
                this.isSaveLoading = true

                await this.$api.ownProductPriceListExtras.post(this.priceListForEdit)

                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.onClose()
                this.onSave()
            } catch (e) {
                this.$toast.error(e.msg || e.errors[0].message || this.$t('error_message.save_error'))
            } finally {
                this.isSaveLoading = false
            }
        }

        addBookingDate() {
            this.priceListForEdit.bookingDates.push({
                condition: null,
                date: null,
            })
        }

        removeBookingDate(bookingDateIndex) {
            this.priceListForEdit.bookingDates.splice(bookingDateIndex, 1)
        }

        onConditionChange(bookingDate) {
            const tempDateFrom = bookingDate.date || bookingDate.dateFrom || null
            const tempDateTo = bookingDate.dateTo || null

            delete bookingDate.date
            delete bookingDate.dateFrom
            delete bookingDate.dateTo

            switch (bookingDate.condition) {
                case 'BEFORE':
                case 'AFTER':
                case 'BEFORE_OR_EQUAL':
                case 'AFTER_OR_EQUAL':
                    bookingDate.date = tempDateFrom
                    break
                default:
                    bookingDate.dateFrom = tempDateFrom
                    bookingDate.dateTo = tempDateTo
                    break
            }
        }

        addTariffLink() {
            this.priceListForEdit.tariffLinks.push({
                tariffId: this.activeTariffs.length ? this.activeTariffs[0].id : null,
                dateFrom: this.activeTariffs.length ? this.activeTariffs[0].dateFrom : null,
                dateTo: this.activeTariffs.length ? this.activeTariffs[0].dateTo : null,
                priority: this.priceListForEdit.tariffLinks.length + 1,
                ...(this.isShowMinMaxNights && {
                    minDays: null,
                    maxDays: null,
                }),
            })
        }

        get isShowMinMaxNights() {
            const availableServices = [contractServiceType.ownHotel, contractServiceType.ownCarRent]
            return availableServices.includes(this.contract.serviceType)
        }

        removeTariffLink(tariffLinkIndex) {
            this.priceListForEdit.tariffLinks.splice(tariffLinkIndex, 1)
        }

        copyTariffLink(tariffLink) {
            this.priceListForEdit.tariffLinks.push({
                tariffId: tariffLink.tariffId,
                dateFrom: tariffLink.dateFrom,
                dateTo: tariffLink.dateTo,
            })
        }

        getExtraService(item) {
            return this.ownProductExtraServices.find(es => es.id === item.extraServiceId)
        }

        get headersPriceExtra() {
            return [
                {title: this.$t('name'), value: 'name', minWidth: '150px'},
                {title: this.$t('status'), value: 'active'},
                {title: this.$t('price_calculation'), value: 'priceCalculationType'},
                {title: this.$t('availability'), value: 'availability'},
                {title: this.$t('include_in_fee'), value: 'includeInFee'},
                {title: this.$t('include_in_mark_up_commission'), value: 'includeInMarkUpAndCommission'},
                {title: this.$t('price'), value: 'amount'},
            ]
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get headersTariffs() {
            if (this.isMobile) {
                return [{title: '', value: 'mobile'}]
            }

            let headers = [
                {title: this.$t('tariff'), value: 'tariff', minWidth: '150px'},
                {title: this.$t('status'), value: 'active'},
                {title: this.$t('dates'), value: 'dates', minWidth: '400px'},
            ]

            if (this.isShowMinMaxNights) {
                headers = [
                    ...headers,
                    {
                        title: this.$t('number_of_nights_min'),
                        value: 'minDays',
                    },
                    {
                        title: this.$t('number_of_nights_max'),
                        value: 'maxDays',
                    },
                ]
            }

            headers = [...headers, {title: this.$t('actions'), value: 'actions', align: 'end'}]

            return headers
        }

        get headersTermsOfUse() {
            if (this.isMobile) {
                return [{title: '', value: 'mobile'}]
            }
            return [
                {title: this.$t('booking_date'), value: 'date_type'},
                {title: this.$t('dates'), value: 'dates'},
                {title: this.$t('actions'), value: 'actions', align: 'end'},
            ]
        }

        get priceExtraAvailabilityItems() {
            return ['REQUIRED', 'OPTIONAL', 'NOT_AVAILABLE'].map(el => ({
                value: el,
                title: this.$t(`price_extra_availability.${el}`),
            }))
        }

        get conditionItems() {
            return ['BETWEEN', 'BEFORE', 'AFTER', 'BEFORE_OR_EQUAL', 'AFTER_OR_EQUAL'].map(el => ({
                value: el,
                title: this.$t(`price_condition.${el}`),
            }))
        }

        priceCalculationNote(item) {
            const extraService = this.getExtraService(item)
            if (!extraService) return ''

            return `${this.$t(`product.info.unitPricingType.${extraService.unitPricingType}`)}, ${this.$t(`priceCalculationType.${extraService.calculationType}`)}`
        }

        get priceCalculationType() {
            let availableTypes
            switch (this.contract.serviceType) {
                case contractServiceType.ownHotel:
                    availableTypes = ['FIRST_NIGHT', 'LAST_NIGHT', 'TOTAL_PRICE']
                    break
                case contractServiceType.ownCarRent:
                case contractServiceType.tour:
                case contractServiceType.ownExtraService:
                case contractServiceType.ownCharter:
                case contractServiceType.ownCruise:
                    availableTypes = ['FIRST_NIGHT', 'TOTAL_PRICE']
                    break
                default:
                    availableTypes = ['TOTAL_PRICE']
                    break
            }
            return availableTypes.map(el => ({
                value: el,
                title: this.$t(`priceCalculationType.${el}`),
            }))
        }

        get tariffCurrency() {
            if (!this.tariffs.length || !this.priceListForEdit?.tariffLinks?.length) return ''
            return this.tariffs.find(({id}) => id === this.priceListForEdit.tariffLinks[0].tariffId)?.currencyCode
        }
    }

    export default ProductPricesExtrasModal
</script>
